//--- ARQUIVO NOVO E COMPLETO: PartialPaymentModal.js ---
import React, { useState, useEffect } from 'react';
import { formatCurrencyDisplay } from './utils.js'; // Importa formatação

// Helpers locais para lidar com input de moeda
const parseCurrencyInput = (value) => {
    const numericString = String(value).replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(numericString) || 0;
};
const formatCurrencyInputMask = (value) => {
    const numericValue = String(value).replace(/[^\d]/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(number);
};


export const PartialPaymentModal = ({ transaction, accounts, onConfirm, onCancel, showNotification }) => {
    // Estado interno do modal
    const [valorPagoInput, setValorPagoInput] = useState('');
    const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0]);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [valorRestante, setValorRestante] = useState(transaction.valor); // Inicializa com o valor total pendente

    // Define a primeira conta como padrão ao carregar
    useEffect(() => {
        if (accounts && accounts.length > 0) {
            setSelectedAccountId(accounts[0].id);
        }
    }, [accounts]);

    // Calcula o valor restante sempre que o valor pago muda
    useEffect(() => {
        const valorPagoNum = parseCurrencyInput(valorPagoInput);
        const totalPendente = transaction.valor || 0;
        setValorRestante(Math.max(0, totalPendente - valorPagoNum)); // Garante que não seja negativo
    }, [valorPagoInput, transaction.valor]);

    // Lida com a mudança no input de valor pago (aplicando máscara)
    const handleValorPagoChange = (e) => {
        setValorPagoInput(formatCurrencyInputMask(e.target.value));
    };

    // Confirmação do pagamento parcial
    const handleConfirm = () => {
        const valorPagoNum = parseCurrencyInput(valorPagoInput);
        const totalPendente = transaction.valor || 0;

        // Validações
        if (!selectedAccountId) {
            showNotification("Selecione a conta.", "error");
            return;
        }
        if (!dataPagamento) {
            showNotification("Selecione a data do pagamento.", "error");
            return;
        }
        if (isNaN(valorPagoNum) || valorPagoNum <= 0) {
            showNotification("Informe um valor pago válido maior que zero.", "error");
            return;
        }
        if (valorPagoNum > totalPendente + 0.001) { // Adiciona pequena tolerância para float point
            showNotification(`O valor pago (${formatCurrencyDisplay(valorPagoNum)}) não pode ser maior que o valor pendente (${formatCurrencyDisplay(totalPendente)}).`, "error");
            return;
        }

        // Chama a função onConfirm passada pelo TransactionsView
        onConfirm({
            valorPago: valorPagoNum,
            dataPagamento: dataPagamento,
            accountId: selectedAccountId
        });
    };

    const inputClasses = "mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500";
    const labelClasses = "block text-sm font-medium text-gray-700 dark:text-gray-300";

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
                <div className="p-6 border-b dark:border-gray-700">
                    <h3 className="text-xl font-bold dark:text-gray-200">Registrar Pagamento Parcial</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Para: "{transaction.descricao}" ({formatCurrencyDisplay(transaction.valor)} pendente)
                    </p>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label htmlFor="valorPagoInput" className={labelClasses}>Valor Pago *</label>
                        <input
                            id="valorPagoInput"
                            name="valorPagoInput"
                            type="text"
                            inputMode="decimal"
                            value={valorPagoInput}
                            onChange={handleValorPagoChange}
                            required
                            placeholder="0,00"
                            className={`${inputClasses} text-right`}
                        />
                    </div>
                     <div>
                         <label htmlFor="dataPagamento" className={labelClasses}>Data do Pagamento *</label>
                         <input
                             id="dataPagamento"
                             name="dataPagamento"
                             type="date"
                             value={dataPagamento}
                             onChange={(e) => setDataPagamento(e.target.value)}
                             required
                             className={inputClasses}
                         />
                     </div>
                     <div>
                         <label htmlFor="selectedAccountId" className={labelClasses}>Conta *</label>
                         <select
                             id="selectedAccountId"
                             name="selectedAccountId"
                             value={selectedAccountId}
                             onChange={(e) => setSelectedAccountId(e.target.value)}
                             required
                             className={inputClasses}
                             disabled={!accounts || accounts.length === 0}
                         >
                             <option value="" disabled>Selecione a conta...</option>
                             {Array.isArray(accounts) && accounts.map(acc => (
                                 <option key={acc.id} value={acc.id}>{acc.name}</option>
                             ))}
                         </select>
                         {(!Array.isArray(accounts) || accounts.length === 0) && (
                            <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">Nenhuma conta cadastrada.</p>
                         )}
                    </div>
                     {/* Mostra o valor restante */}
                     <div className="pt-2 text-right">
                         <p className="text-sm text-gray-500 dark:text-gray-400">
                             Valor Restante: <span className="font-semibold">{formatCurrencyDisplay(valorRestante)}</span>
                         </p>
                     </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 flex justify-end space-x-4 rounded-b-lg">
                    <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-md">Cancelar</button>
                    <button type="button" onClick={handleConfirm} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Confirmar Pagamento</button>
                </div>
            </div>
        </div>
    );
};